// Errors+warnings contract helpers (client side).
//
// Business logic can return structured errors AND warnings (type 'E' blocks,
// type 'W' may be acknowledged). When a blocked call failed ONLY on warnings
// the server answers 422 with code VALIDATION_WARNINGS — the client offers
// "ignore warnings and proceed" and retries the SAME call with the
// acknowledged codes in `ignore_warnings`. These helpers implement that
// detection + retry loop for every surface (designer preview, site embeds,
// standalone runtime).

import type { ValidationErrorItem, WarningItem } from './contracts'

interface AnyErrShape {
  // Designer / site (axios): response.data = {error: {code, message, details}}
  response?: {
    status?: number
    data?: {
      error?: {
        code?: string
        details?: { errors?: ValidationErrorItem[]; warnings?: WarningItem[] }
      }
    }
  }
  // Standalone runtime (fetch ApiError): status + detail = {errors, summary,
  // code, warnings, scope}
  status?: number
  detail?: unknown
}

function toWarningItem(e: ValidationErrorItem | WarningItem): WarningItem {
  return {
    code: (e as ValidationErrorItem).code ?? null,
    field: (e as ValidationErrorItem).field ?? null,
    message: e.message || '',
    ...((e as ValidationErrorItem).scope ? { scope: (e as ValidationErrorItem).scope } : {}),
  }
}

/**
 * When `err` is a 422 whose outstanding entries are ALL ignorable warnings,
 * return them (so the caller can show a "proceed anyway?" confirm); otherwise
 * return null (a real error — rethrow / display as today). Handles both error
 * envelopes: the designer/site `{error:{code,details}}` and the standalone
 * runtime's flat `{errors, summary, code}` detail.
 */
export function extractBlockingWarnings(err: unknown): WarningItem[] | null {
  const e = err as AnyErrShape
  // Designer / site envelope.
  const env = e?.response?.data?.error
  if (env && typeof env === 'object') {
    if (e.response?.status !== 422) return null
    const details = env.details || {}
    if (env.code === 'VALIDATION_WARNINGS') {
      const list = details.warnings?.length ? details.warnings : (details.errors || [])
      return list.map(toWarningItem)
    }
    return warningsWhenAllW(details.errors)
  }
  // Standalone runtime flat detail.
  if (e?.status === 422 && e.detail && typeof e.detail === 'object') {
    const d = e.detail as {
      code?: string
      errors?: ValidationErrorItem[]
      warnings?: WarningItem[]
    }
    if (d.code === 'VALIDATION_WARNINGS') {
      const list = d.warnings?.length ? d.warnings : (d.errors || [])
      return list.map(toWarningItem)
    }
    return warningsWhenAllW(d.errors)
  }
  return null
}

// Legacy-server fallback: no `code` on the envelope, but every validation
// entry is severity 'W' → still offer the proceed flow.
function warningsWhenAllW(errors?: ValidationErrorItem[]): WarningItem[] | null {
  if (!errors || !errors.length) return null
  if (!errors.every((x) => x.severity === 'W')) return null
  return errors.map(toWarningItem)
}

/** The identity sent back in `ignore_warnings` for one warning. */
export function warningCode(w: WarningItem): string {
  return (w.code && String(w.code).trim()) || (w.message || '').trim()
}

/**
 * Run `attempt(ignore)`; when it fails on warnings-only, ask `confirm` and —
 * if the user proceeds — retry with the accumulated warning codes. Codes
 * accumulate across rounds (a retry may surface NEW warnings, which prompt
 * again, up to `maxRounds`). Any non-warning failure (or a declined confirm)
 * rethrows to the caller's normal error handling.
 *
 * `initialIgnore` seeds the codes already acknowledged elsewhere (a detail
 * row's OK), so they are not asked again.
 */
export async function attemptWithWarningConfirm<T>(
  attempt: (ignoreWarnings: string[]) => Promise<T>,
  confirm: (warnings: WarningItem[]) => Promise<boolean>,
  maxRounds = 3,
  initialIgnore: string[] = [],
): Promise<T> {
  let ignore: string[] = Array.from(new Set(initialIgnore.filter(Boolean)))
  // First try + up to maxRounds retries-with-ignore.
  for (let round = 0; ; round++) {
    try {
      return await attempt(ignore)
    } catch (err) {
      const warns = round < maxRounds ? extractBlockingWarnings(err) : null
      if (!warns || !warns.length) throw err
      const proceed = await confirm(warns)
      if (!proceed) throw err
      const codes = warns.map(warningCode).filter(Boolean)
      ignore = Array.from(new Set([...ignore, ...codes]))
    }
  }
}

// ── Reason for change (21 CFR 11.10(e), Phase 0.3) ─────────────────────────
//
// The same detection-and-retry loop as the warnings flow above, for the other
// thing a save can stop and ask for. Kept beside it deliberately: a surface
// that already knows how to handle "the server needs an answer before this can
// complete" handles this by adding one line rather than learning a second
// shape.

/** The field a REASON_REQUIRED failure names. Not a real column. */
export const REASON_FIELD = '__reason__'

/**
 * When `err` is the server asking for a reason for change, return its message;
 * otherwise null.
 *
 * Detected on the CODE, never on the message text — a message is wording and
 * wording gets improved, while a code is a contract. Both error envelopes are
 * read for the same reason `extractBlockingWarnings` reads both.
 */
export function extractReasonDemand(err: unknown): string | null {
  const e = err as AnyErrShape
  const pick = (list?: ValidationErrorItem[]): string | null => {
    const hit = (list || []).find(
      (x) => x.code === 'REASON_REQUIRED' || x.field === REASON_FIELD)
    return hit ? (hit.message || 'This change needs a reason.') : null
  }
  const env = e?.response?.data?.error
  if (env && typeof env === 'object') {
    return pick((env.details || {}).errors)
  }
  if (e?.detail && typeof e.detail === 'object') {
    return pick((e.detail as { errors?: ValidationErrorItem[] }).errors)
  }
  return null
}

/**
 * Run `attempt`; when the server asks for a reason, ask the person and retry
 * once with it.
 *
 * Exactly once. A second refusal after a reason has been given is not another
 * prompt — it means something else is wrong, and asking again would be a loop
 * the person cannot get out of.
 */
export async function attemptWithReason<T>(
  attempt: (reason: string, reasonCode: string) => Promise<T>,
  prompt: (message: string)
    => Promise<{ reason: string; reasonCode: string } | null>,
): Promise<T> {
  try {
    return await attempt('', '')
  } catch (err) {
    const demand = extractReasonDemand(err)
    if (!demand) throw err
    const answer = await prompt(demand)
    // A declined prompt rethrows the ORIGINAL refusal, so the caller's error
    // path shows what the server said rather than a silent no-op.
    if (!answer) throw err
    return await attempt(answer.reason, answer.reasonCode)
  }
}
